import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import {
  Button, Card, Dialog, EmptyState, ErrorState, Field, Input, Loading, Select,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';

const AUDIENCES = { both: 'Rental and broker apps', rental: 'Rental app only', broker: 'Broker app only' };
const LANGUAGES = { all: 'All languages', en: 'English', hi: 'हिन्दी (Hindi)' };
const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 5 * 1024 * 1024;
const KEY = ['admin-home-banners'];

/**
 * The home-screen carousel of the rental and broker apps.
 *
 * Static images only, in the order shown here. Upload, switch off, reorder or
 * delete; the apps read the active list on their next load.
 */
export default function BannersPage() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CONFIG_MANAGE);
  const queryClient = useQueryClient();
  const fileInput = useRef(null);
  const desktopInput = useRef(null);
  const rowDesktopInput = useRef(null);
  const rowTarget = useRef(null);

  const banners = useQuery({ queryKey: KEY, queryFn: api.banners.list });
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState('both');
  const [language, setLanguage] = useState('all');
  const [file, setFile] = useState(null);
  const [desktopFile, setDesktopFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [removing, setRemoving] = useState(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
  const onError = (error) => toast.error(error.message);

  const update = useMutation({
    mutationFn: ({ id, body }) => api.banners.update(id, body),
    onSuccess: refresh,
    onError,
  });
  const reorder = useMutation({ mutationFn: (ids) => api.banners.reorder(ids), onSuccess: refresh, onError });
  const remove = useMutation({
    mutationFn: (id) => api.banners.remove(id),
    onSuccess: () => {
      toast.success('Banner deleted.');
      setRemoving(null);
      refresh();
    },
    onError,
  });

  /** A chosen image if it is an allowed type and size, else null (with the reason toasted). */
  const acceptable = (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return null;
    if (!ACCEPT.split(',').includes(chosen.type)) {
      toast.error('Use a JPG, PNG or WebP image.');
      event.target.value = '';
      return null;
    }
    if (chosen.size > MAX_BYTES) {
      toast.error('That image is over 5MB. Use a smaller one.');
      event.target.value = '';
      return null;
    }
    return chosen;
  };

  const pickFile = (event) => {
    const chosen = acceptable(event);
    if (!chosen) return;
    setFile(chosen);
    if (!title.trim()) setTitle(chosen.name.replace(/\.[^.]+$/, ''));
  };

  /** Slot, then the bytes straight to storage, then confirm. Returns the asset id. */
  const uploadImage = async (image) => {
    const slot = await api.banners.slot({ fileName: image.name, mimeType: image.type, byteSize: image.size });
    const put = await fetch(slot.uploadUrl, {
      method: slot.method || 'PUT',
      headers: slot.headers || { 'Content-Type': image.type },
      body: image,
    });
    if (!put.ok) throw new Error('The image could not be uploaded. Try again.');
    await api.banners.complete(slot.assetId);
    return slot.assetId;
  };

  const add = async () => {
    if (!file || !title.trim()) return;
    setIsUploading(true);
    try {
      const assetId = await uploadImage(file);
      const desktopAssetId = desktopFile ? await uploadImage(desktopFile) : undefined;
      await api.banners.create({ assetId, desktopAssetId, title: title.trim(), audience, language });
      toast.success('Banner added to the carousel.');
      setFile(null);
      setDesktopFile(null);
      setTitle('');
      if (fileInput.current) fileInput.current.value = '';
      if (desktopInput.current) desktopInput.current.value = '';
      refresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  /** Adds or replaces the wide desktop image of an existing banner. */
  const setDesktopImage = async (event) => {
    const image = acceptable(event);
    const target = rowTarget.current;
    event.target.value = '';
    if (!image || !target) return;
    try {
      const desktopAssetId = await uploadImage(image);
      await api.banners.update(target, { desktopAssetId });
      toast.success('Desktop image saved.');
      refresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const list = banners.data || [];
  const move = (index, delta) => {
    const ids = list.map((banner) => banner._id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  return (
    <div className="stack">
      <Card
        title="Home carousel"
        subtitle="Static images shown at the top of the home screen in the rental and broker apps, in this order. With none active, the carousel is hidden."
      >
        {canManage ? (
          <div className="stack">
            <div className="field-row">
              <Field label="Phone image" hint="JPG, PNG or WebP, up to 5MB. 16:9 (for example 1600×900) fits best; it is cropped to fill." id="banner-file">
                <input id="banner-file" ref={fileInput} type="file" accept={ACCEPT} onChange={pickFile} disabled={isUploading} />
              </Field>
              <Field
                label="Desktop image (optional)"
                hint="A wide version for browsers on a computer, about 4:1 (for example 2400×600), with the text in the middle. Without one, desktop shows the image above."
                id="banner-desktop-file"
              >
                <input
                  id="banner-desktop-file"
                  ref={desktopInput}
                  type="file"
                  accept={ACCEPT}
                  disabled={isUploading}
                  onChange={(event) => {
                    const chosen = acceptable(event);
                    if (chosen) setDesktopFile(chosen);
                  }}
                />
              </Field>
              <Field label="Name" hint="For your reference and screen readers; not shown on the image." id="banner-title">
                <Input id="banner-title" value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} />
              </Field>
              <Field label="Shown in" id="banner-audience">
                <Select id="banner-audience" value={audience} onChange={(event) => setAudience(event.target.value)}>
                  {Object.entries(AUDIENCES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Language" hint="Shown only to people using the app in this language." id="banner-language">
                <Select id="banner-language" value={language} onChange={(event) => setLanguage(event.target.value)}>
                  {Object.entries(LANGUAGES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="row">
              <Button variant="primary" onClick={add} loading={isUploading} disabled={!file || !title.trim()}>
                Upload banner
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <input ref={rowDesktopInput} type="file" accept={ACCEPT} hidden onChange={setDesktopImage} />

      <Card title={`Banners${list.length ? ` (${list.length})` : ''}`}>
        {banners.isLoading ? (
          <Loading label="Loading banners" />
        ) : banners.isError ? (
          <ErrorState error={banners.error} onRetry={banners.refetch} />
        ) : !list.length ? (
          <EmptyState title="No banners yet" body="Upload an image above and it appears in the apps." />
        ) : (
          <div className="stack">
            {list.map((banner, index) => (
              <div className="row" key={banner._id} style={{ alignItems: 'center', gap: 16, opacity: banner.active ? 1 : 0.55 }}>
                <img
                  src={banner.imageUrl}
                  alt={banner.title}
                  style={{ width: 192, height: 108, objectFit: 'cover', borderRadius: 8, background: '#eee' }}
                />
                {banner.desktopImageUrl ? (
                  <img
                    src={banner.desktopImageUrl}
                    alt={`${banner.title} (desktop)`}
                    style={{ width: 240, height: 60, objectFit: 'cover', borderRadius: 8, background: '#eee' }}
                  />
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{banner.title}</strong>
                  <div className="field__hint">
                    {`${index + 1}. ${AUDIENCES[banner.audience]} · ${LANGUAGES[banner.language || 'all']}${banner.active ? '' : ' · switched off'}`}
                  </div>
                </div>
                {canManage ? (
                  <div className="row">
                    <Select
                      aria-label={`Where ${banner.title} is shown`}
                      value={banner.audience}
                      onChange={(event) => update.mutate({ id: banner._id, body: { audience: event.target.value } })}
                    >
                      {Object.entries(AUDIENCES).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Select>
                    <Select
                      aria-label={`Language of ${banner.title}`}
                      value={banner.language || 'all'}
                      onChange={(event) => update.mutate({ id: banner._id, body: { language: event.target.value } })}
                    >
                      {Object.entries(LANGUAGES).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Select>
                    <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)} aria-label="Move up">↑</Button>
                    <Button size="sm" variant="ghost" disabled={index === list.length - 1} onClick={() => move(index, 1)} aria-label="Move down">↓</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        rowTarget.current = banner._id;
                        rowDesktopInput.current?.click();
                      }}
                    >
                      {banner.desktopImageUrl ? 'Replace desktop image' : 'Add desktop image'}
                    </Button>
                    {banner.desktopImageUrl ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => update.mutate({ id: banner._id, body: { desktopAssetId: null } })}
                      >
                        Remove desktop image
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => update.mutate({ id: banner._id, body: { active: !banner.active } })}
                    >
                      {banner.active ? 'Switch off' : 'Switch on'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(banner)}>Delete</Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title="Delete this banner?"
        warning="It disappears from the apps on their next load. The image is removed and cannot be recovered."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
            <Button variant="primary" loading={remove.isPending} onClick={() => remove.mutate(removing._id)}>Delete</Button>
          </>
        }
      >
        {removing ? <strong>{removing.title}</strong> : null}
      </Dialog>
    </div>
  );
}
